import type { PublicListing } from '@/services/publicApi';

const UPLOADS_BASE = (() => {
  const uploadBase = import.meta.env.VITE_UPLOAD_BASE_URL;
  const apiBase = import.meta.env.VITE_PUBLIC_API_URL;
  return uploadBase || apiBase || '';
})();

export interface ListingCard {
  id: number;
  title: string;
  slug: string;
  location: string;
  city: string;
  stateProvince: string;
  sizeAcres: number;
  sizeDisplay: string;
  expectedRent: number;
  expectedRentDisplay: string;
  category: string;
  status: string;
  statusColor: string;
  description: string;
  useCases: string[];
  features: string[];
  images: string[];
  postedDate: string;
  viewCount: number;
  isFeatured: boolean;
}

const STATUS_COLORS: Record<string, string> = {
  available: 'bg-teal-500/15 border-teal-500/25 text-teal-400',
  under_review: 'bg-accent-500/15 border-accent-500/25 text-accent-700',
  leased: 'bg-primary-500/15 border-primary-500/25 text-primary-700',
  sold: 'bg-rose-500/15 border-rose-500/25 text-rose-400',
  off_market: 'bg-foreground-500/15 border-foreground-500/25 text-foreground-400',
};

const STATUS_LABELS: Record<string, string> = {
  available: 'Available',
  under_review: 'Under Review',
  leased: 'Leased',
  sold: 'Sold',
  off_market: 'Off Market',
};

const CATEGORY_LABELS: Record<string, string> = {
  commercial: 'Commercial',
  industrial: 'Industrial',
  agricultural: 'Agricultural',
  residential: 'Residential',
  mixed_use: 'Mixed Use',
};

function formatRent(rent: number, currency: string, period: string): string {
  // Always display in Indian Rupees regardless of stored currency setting
  const formatter = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  });
  return `${formatter.format(rent)}/${period || 'mo'}`;
}

function formatSize(acres: number): string {
  if (acres >= 1) {
    return `${acres} ${acres === 1 ? 'Acre' : 'Acres'}`;
  }
  return `${(acres * 43560).toFixed(0)} Sq Ft`;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function mapListingToCard(listing: PublicListing): ListingCard {
  return {
    id: listing.id,
    title: listing.title,
    slug: listing.slug,
    location: listing.location,
    city: listing.city || '',
    stateProvince: listing.state_province || '',
    sizeAcres: listing.size_acres,
    sizeDisplay: formatSize(listing.size_acres),
    expectedRent: listing.expected_rent,
    expectedRentDisplay: formatRent(listing.expected_rent, listing.rent_currency, listing.rent_period),
    category: CATEGORY_LABELS[listing.category] || listing.category,
    status: STATUS_LABELS[listing.status] || listing.status,
    statusColor: STATUS_COLORS[listing.status] || STATUS_COLORS.available,
    description: listing.description || '',
    useCases: listing.use_cases || [],
    features: listing.features || [],
    images: buildImageUrls(listing.images_json),
    postedDate: formatDate(listing.posted_date),
    viewCount: listing.view_count || 0,
    isFeatured: listing.is_featured === 1,
  };
}

function buildImageUrls(paths: string[] | null): string[] {
  if (!paths || !Array.isArray(paths)) return [];
  const base = UPLOADS_BASE.endsWith('/') ? UPLOADS_BASE.slice(0, -1) : UPLOADS_BASE;
  return paths
    .filter((p): p is string => typeof p === 'string' && p.length > 0)
    .map((p) => {
      if (p.startsWith('data:')) return p;
      if (p.startsWith('http://') || p.startsWith('https://')) return p;
      if (p.startsWith('/uploads/')) return `${base}${p}`;
      if (p.startsWith('uploads/')) return `${base}/${p}`;
      return '';
    })
    .filter((p) => p.length > 0);
}

export const categoryFilters = [
  { label: 'All', value: 'All', icon: 'ri-grid-line' },
  { label: 'Buy', value: 'sale', icon: 'ri-shopping-cart-line' },
  { label: 'JV', value: 'jv', icon: 'ri-team-line' },
  { label: 'Lease', value: 'lease', icon: 'ri-article-line' },
  { label: 'Development', value: 'development', icon: 'ri-building-2-line' },
  { label: 'Rent', value: 'rent', icon: 'ri-home-line' },
  { label: 'Any', value: 'any', icon: 'ri-circle-line' },
];